import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const HeroSection = () => {
  const navigate = useNavigate();

  const handleLaunchAgent = () => {
    navigate('/launch-agent');
  };

  const handleAccessDashboard = () => {
    navigate('/dashboard');
  };

  return (
    <section className="relative min-h-screen bg-gradient-to-br from-background via-muted/30 to-background overflow-hidden">
      {/* Animated Background Globe */}
      <div className="absolute inset-0 flex items-center justify-center opacity-10">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
          className="w-96 h-96 md:w-[600px] md:h-[600px] lg:w-[800px] lg:h-[800px]"
        >
          <div className="relative w-full h-full">
            {/* Globe wireframe */}
            <div className="absolute inset-0 border-2 border-primary/20 rounded-full"></div>
            <div className="absolute inset-4 border border-primary/15 rounded-full"></div>
            <div className="absolute inset-8 border border-primary/10 rounded-full"></div>
            
            {/* Network nodes */}
            {[...Array(12)]?.map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 bg-accent rounded-full"
                style={{
                  top: `${20 + (i * 5)}%`,
                  left: `${15 + (i * 6)}%`,
                }}
                animate={{
                  scale: [1, 1.5, 1],
                  opacity: [0.3, 1, 0.3],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  delay: i * 0.2,
                }}
              />
            ))}
            
            {/* Connecting lines */}
            <svg className="absolute inset-0 w-full h-full">
              {[...Array(8)]?.map((_, i) => (
                <motion.line
                  key={i}
                  x1={`${20 + i * 10}%`}
                  y1={`${30 + i * 8}%`}
                  x2={`${40 + i * 8}%`}
                  y2={`${50 + i * 6}%`}
                  stroke="var(--color-primary)"
                  strokeWidth="1"
                  opacity="0.2"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    repeatType: "reverse",
                    delay: i * 0.3,
                  }}
                />
              ))}
            </svg>
          </div>
        </motion.div>
      </div>
      {/* Hero Content */}
      <div className="relative z-10 container mx-auto px-6 pt-24 pb-16 flex items-center min-h-screen">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-8"
          >
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-foreground mb-6 leading-tight">
              Ipsos Survey Data
              <span className="block text-transparent bg-gradient-to-r from-primary to-secondary bg-clip-text">
                Intelligence Platform
              </span>
            </h1>
            
            <p className="text-lg md:text-xl lg:text-2xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
              Internal platform for Ipsos agents to scrape survey data, analyze public responses, generate client projections, and deliver comprehensive market research insights.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12"
          >
            <Button
              variant="default"
              size="lg"
              onClick={handleLaunchAgent}
              iconName="Rocket"
              iconPosition="left"
              className="w-full sm:w-auto px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Launch Agent
            </Button>
            
            <Button
              variant="outline"
              size="lg"
              onClick={handleAccessDashboard}
              iconName="BarChart3"
              iconPosition="left"
              className="w-full sm:w-auto px-8 py-4 text-lg font-semibold border-2 hover:bg-muted transition-all duration-300"
            >
              Access Dashboard
            </Button>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-wrap justify-center items-center gap-6 text-sm text-text-secondary"
          >
            <div className="flex items-center gap-2">
              <Icon name="Shield" size={16} className="text-success" />
              <span>Secure Platform</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="Database" size={16} className="text-success" />
              <span>Multi-Source Data</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="Globe" size={16} className="text-success" />
              <span>Global Survey Access</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="Zap" size={16} className="text-success" />
              <span>Real-time Processing</span>
            </div>
          </motion.div>
        </div>
      </div>
      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)]?.map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-accent/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [-20, -100],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: Math.random() * 3 + 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>
    </section>
  );
};

export default HeroSection;